import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { usePostHog } from 'posthog-js/react';
import { useAtom } from 'jotai';

import { campaignTransitionInitiatorAtom } from '@/store';
import { updateCampaignState } from '@/lib/apis';
import { applyCampaignStateToLists, findCampaignSlugInLists } from '@/lib/campaign-cache-updates';
import { campaignTaskTrackingQueryOptions } from '@/lib/query-options';
import { CAMPAIGN_STATE, type CampaignData } from '@/lib/types';
import { toast } from '@/hooks/useToast';

/**
 * Dispatch a state transition and poll the task_tracking summary
 * until the matching task reaches a terminal state. Shows a toast on completion.
 */
export function useCampaignStateTransition(
  campaignId: number,
  currentCampaignState: CAMPAIGN_STATE | undefined,
  source?: string,
) {
  const queryClient = useQueryClient();
  const posthog = usePostHog();
  // Attribution of the in-flight transition to the surface that dispatched it, so the
  // Go Live and Deploy buttons only spin for transitions they own (#5663). Shared via
  // atom because each button mounts its own instance of this hook; the shared TARGET is
  // authoritative for instances that adopted the task rather than dispatching it (#5669).
  const [transitionInitiators, setTransitionInitiators] = useAtom(campaignTransitionInitiatorAtom);
  const pendingSource = transitionInitiators[campaignId]?.source ?? null;
  const sharedTarget = (transitionInitiators[campaignId]?.target ?? null) as CAMPAIGN_STATE | null;
  const [pendingTaskId, setPendingTaskId] = useState<number | null>(null);
  // Reactive copy of the active target so consumers can distinguish PAUSED vs LIVE transitions.
  const [pendingTarget, setPendingTarget] = useState<CAMPAIGN_STATE | null>(null);
  // Target captured by the dispatch path: known at click time.
  const pendingTargetRef = useRef<CAMPAIGN_STATE | null>(null);
  // Target inferred on resume: the service writes campaign.state BEFORE the GA sync,
  // so on first observation of an in-flight task, current campaign.state is the target
  // (until rollback on failure — which is why we snapshot, not re-read at completion).
  const resumedTargetRef = useRef<CAMPAIGN_STATE | null>(null);
  // Guards against firing the completion toast off a stale poll snapshot. The cleanup
  // effect can run on cached `in_progress=0, total>0` data from a previous task before
  // the post-dispatch refetch lands; require seeing in_progress>0 at least once first.
  const seenInProgressRef = useRef(false);

  const mutation = useMutation({
    mutationFn: async (newState: CAMPAIGN_STATE) => {
      pendingTargetRef.current = newState;
      setPendingTarget(newState);
      return updateCampaignState(campaignId, newState);
    },
    onMutate: (newState) => {
      setTransitionInitiators((prev) => ({ ...prev, [campaignId]: { source, target: newState } }));
      posthog?.capture('campaign_state_transition_attempted', {
        campaign_id: campaignId,
        target_state: newState,
        source,
      });
    },
    onSuccess: (data) => {
      seenInProgressRef.current = false;
      setPendingTaskId(data.task_tracking_id);
      // Broad prefix invalidate: the query key is ['campaignTaskTracking', adCreation, campaignId];
      // a [..., campaignId] filter does not match because element-wise prefix matching
      // compares position-by-position and `campaignId !== adCreation`.
      void queryClient.invalidateQueries({ queryKey: ['campaignTaskTracking'] });
    },
    onError: (err) => {
      pendingTargetRef.current = null;
      setPendingTarget(null);
      setTransitionInitiators((prev) => ({ ...prev, [campaignId]: undefined }));
      toast({
        description: err instanceof Error ? err.message : 'Failed to start state update',
        variant: 'destructive',
      });
    },
  });

  // Always poll task tracking — the query options stop polling when nothing is in
  // progress, so the cost when idle is one fetch per mount. Keeping it enabled lets
  // us detect a state-update task that started in a previous mount (page reload,
  // navigation, tab switch) and resume showing pending UI + completion toast.
  const trackingQuery = useQuery({
    ...campaignTaskTrackingQueryOptions(campaignId, 'state-update'),
  });

  // Sentinel id used when we resume polling for a task we did not dispatch ourselves
  // (the actual task_tracking_id is not surfaced by the summary endpoint).
  const RESUMED_TASK_SENTINEL = -1;

  // Resume polling on mount if the backend already has an in-flight state-update task.
  useEffect(() => {
    if (pendingTaskId !== null) return;
    // A dispatch from THIS instance is in flight (mutationFn already captured the real
    // target) — a poll landing before onSuccess must not adopt the task as foreign and
    // overwrite pendingTarget with the current state (#5669).
    if (mutation.isPending) return;
    const summary = trackingQuery.data?.campaign_state_update;
    if (summary && summary.in_progress > 0) {
      setPendingTaskId(RESUMED_TASK_SENTINEL);
      // Prefer the target shared by the dispatching instance (page toolbar vs actions
      // dropdown each mount their own hook, #5669); fall back to snapshotting current
      // campaign.state — the service commits it before the GA sync, so on a true resume
      // (post-reload) it is the target. Captured now so failure rollback can't change it.
      resumedTargetRef.current = sharedTarget ?? currentCampaignState ?? null;
      setPendingTarget(sharedTarget ?? currentCampaignState ?? null);
    }
  }, [trackingQuery.data, pendingTaskId, currentCampaignState, mutation.isPending, sharedTarget]);

  // Track whether we've ever seen the in-flight task in the poll. Without this the
  // cleanup effect can fire on a stale `in_progress=0, total>0` snapshot from before
  // the dispatch — the post-onSuccess invalidate hasn't refetched yet.
  useEffect(() => {
    if ((trackingQuery.data?.campaign_state_update?.in_progress ?? 0) > 0) {
      seenInProgressRef.current = true;
    }
  }, [trackingQuery.data]);

  useEffect(() => {
    if (pendingTaskId === null || !trackingQuery.data) return;
    const summary = trackingQuery.data.campaign_state_update;
    if (!summary) return;

    // Require total > 0 to avoid acting on a stale poll where the row is not yet visible.
    const isDone = summary.in_progress === 0 && summary.total > 0;
    if (!isDone) return;
    if (!seenInProgressRef.current) return;

    // Prefer the dispatch-time target; fall back to the resume snapshot.
    const target = pendingTargetRef.current ?? resumedTargetRef.current;
    const failed = summary.failed > 0;
    if (failed) {
      const apiMessage = summary.latest_error_message?.trim();
      const truncated = apiMessage && apiMessage.length > 250 ? `${apiMessage.slice(0, 250)}…` : apiMessage;
      const prefix = target === CAMPAIGN_STATE.LIVE ? "Couldn't launch campaign" : "Couldn't update state";
      const fallback = target === CAMPAIGN_STATE.LIVE ? 'Launch failed. Please try again.' : 'State update failed.';
      toast({
        description: truncated ? `${prefix}. Reason: ${truncated}` : fallback,
        variant: 'destructive',
      });
      posthog?.capture('campaign_state_transition_failed', {
        campaign_id: campaignId,
        target_state: target,
        source,
      });
    } else {
      posthog?.capture('campaign_state_transition_succeeded', {
        campaign_id: campaignId,
        target_state: target,
        source,
      });
      // Optimistically flip the campaign's state in the cached ['campaigns'] lists so the
      // sidebar play/pause icon updates with the confirmation. Without this the icon lags
      // by seconds: the heavy ['campaigns'] payload (every campaign + nested ads/assets) is
      // slow to refetch, so the light ['campaign', slug] detail view flips to Live first
      // while the sidebar waits on the list. The invalidate below reconciles in the
      // background. The updater guards non-array sibling queries the ['campaigns'] prefix
      // also matches (see applyCampaignStateToLists).
      //
      // Self-dispatched ONLY: pendingTargetRef is authoritative (captured at click), but
      // resumedTargetRef is a snapshot GUESS — wrong whenever the adopted task (e.g. the
      // pause/resume toggle's) was still queued at first observation. Patching caches
      // with a stale guess flip-flops the page state and fires duplicate / vanishing
      // Paused/Resumed confirmations (#5669). Foreign tasks rely on the invalidations.
      if (target && pendingTargetRef.current) {
        queryClient.setQueriesData<CampaignData[]>({ queryKey: ['campaigns'] }, (old) =>
          applyCampaignStateToLists(old, campaignId, target),
        );
        // Patch the detail cache too: the service commits state (and first_published_at
        // on a launch) at task START, but the invalidate below lands a beat after
        // isPending drops. In that gap a stale DRAFT detail row re-mounts the static
        // Go Live button — the end-of-launch flash (#5664).
        const slug = findCampaignSlugInLists(queryClient.getQueriesData({ queryKey: ['campaigns'] }), campaignId);
        if (slug) {
          queryClient.setQueryData<CampaignData>(['campaign', slug], (old) =>
            old
              ? {
                  ...old,
                  state: target,
                  first_published_at:
                    target === CAMPAIGN_STATE.LIVE
                      ? (old.first_published_at ?? new Date().toISOString())
                      : old.first_published_at,
                }
              : old,
          );
        }
      }
    }
    setPendingTaskId(null);
    pendingTargetRef.current = null;
    resumedTargetRef.current = null;
    setPendingTarget(null);
    seenInProgressRef.current = false;
    setTransitionInitiators((prev) => ({ ...prev, [campaignId]: undefined }));
    void queryClient.invalidateQueries({ queryKey: ['campaigns'] });
    void queryClient.invalidateQueries({ queryKey: ['campaign'] });
  }, [pendingTaskId, trackingQuery.data, campaignId, source, posthog, queryClient, setTransitionInitiators]);

  return {
    dispatch: mutation.mutateAsync,
    isPending: pendingTaskId !== null || mutation.isPending,
    // Shared target wins: an instance that adopted the task only has a snapshot guess,
    // while the dispatching instance published the real target to the atom (#5669).
    pendingTarget: sharedTarget ?? pendingTarget,
    // Source string of the surface that dispatched the in-flight transition; null when
    // nothing is in flight or the transition was resumed after a reload (owner unknown).
    pendingSource,
  };
}
